/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  fetchLabOrders,
  deleteLabOrder,
  updateLabOrder,
} from '@store/actions/lab.action'
import { RootState } from '@store/index'

import LabSharedTable from './LabSharedTable'
import LabExpandedContent from './LabExpandedContent'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import LabOrderInfo from '@components/layout/Modals/LabOrderInfo/LabOrderInfo.component'
import { LabOrder } from '@dataTypes/lab.types'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import { useAppDispatch } from '@store/hooks'

interface LabTrackerProps {
  searchQuery: string
  filters: Record<string, string[]>
}

const LabTracker: React.FC<LabTrackerProps> = ({ searchQuery, filters }) => {
  const dispatch = useAppDispatch()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null)
  const [editableRow, setEditableRow] = useState<any>(null)
  const [selectedOrder, setSelectedOrder] = useState<LabOrder | null>(null)
  const [showOrderInfo, setShowOrderInfo] = useState<boolean>(false)

  const labOrders = useSelector(
    (state: RootState) => state.lab.labOrders.orders,
  )

  useEffect(() => {
    dispatch(fetchLabOrders())
  }, [dispatch])

  const toggleRowExpansion = (id: string): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const filteredLabOrders = labOrders.filter((order) => {
    const q = searchQuery.toLowerCase()
    const matchesSearch =
      order.orderNo?.toLowerCase().includes(q) ||
      order.patientName?.toLowerCase().includes(q) ||
      order.description?.toLowerCase().includes(q)

    const statusFilter = filters.status?.length
      ? filters.status.includes(order.status)
      : true

    return matchesSearch && statusFilter
  })

  const columns = [
    {
      key: 'orderNo',
      label: 'Order No.',
      sortable: true,
      style: { width: '200px' },
      formatter: (_: any, row: any) => (
        <div className="toggle-invoice-container d-flex align-items-center justify-content-between">
          <span>{row.orderNo}</span>
          <button
            className={`expand-button ${expandedRows.has(row.orderNo) ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              toggleRowExpansion(row.orderNo)
            }}
          >
            {expandedRows.has(row.orderNo) ? '▼' : '▶'}
          </button>
        </div>
      ),
    },
    {
      key: 'requestedOn',
      label: 'Requested On',
      sortable: true,
      style: { width: '150px' },
    },
    {
      key: 'patientName',
      label: 'Patient Name',
      sortable: true,
      style: { width: '140px' },
    },
    {
      key: 'totalAmount',
      label: 'Total Amount',
      sortable: true,
      style: { width: '130px' },
      formatter: (val: any, row: any) =>
        editingOrderId === row.orderNo ? (
          <input
            type="number"
            className="form-control form-control-sm"
            value={editableRow?.totalAmount ?? 0}
            onChange={(e) =>
              setEditableRow({
                ...editableRow,
                totalAmount: parseFloat(e.target.value),
              })
            }
          />
        ) : (
          `$${val.toFixed(2)}`
        ),
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      style: { width: '230px' },
      formatter: (val: any, row: any) =>
        editingOrderId === row.orderNo ? (
          <input
            type="text"
            className="form-control form-control-sm"
            value={editableRow?.description ?? ''}
            onChange={(e) =>
              setEditableRow({
                ...editableRow,
                description: e.target.value,
              })
            }
          />
        ) : (
          val
        ),
    },
    {
      key: 'completionDate',
      label: 'Completion Date',
      sortable: true,
      style: { width: '160px' },
      formatter: (val: string, row: LabOrder) =>
        row.status === 'Cancelled' ? '-' : val || '-',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      style: { width: '120px' },
      formatter: (val: any, row: LabOrder) =>
        editingOrderId === row.orderNo ? (
          <select
            className="form-select form-select-sm"
            value={editableRow?.status ?? ''}
            onChange={(e) =>
              setEditableRow({
                ...editableRow,
                status: e.target.value as LabOrder['status'],
              })
            }
          >
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Preparing">Preparing</option>
            <option value="Out For Delivery">Out For Delivery</option>
          </select>
        ) : (
          <StatusBadge status={val as StatusTypes} />
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      style: { width: '150px', textAlign: 'center' as const },
      formatter: (_: any, row: any) => (
        <td className="actions-column text-nowrap align-middle text-center">
          {editingOrderId === row.orderNo ? (
            <div
              className="d-flex justify-content-center align-items-center gap-0"
              style={{ height: '32px' }}
            >
              <button
                className="action-button"
                title="Save"
                onClick={() => {
                  dispatch(updateLabOrder(editableRow))
                  setEditingOrderId(null)
                  setEditableRow(null)
                }}
              >
                <i className="ti ti-check text-success fs-4"></i>
              </button>
              <button
                className="action-button"
                title="Cancel"
                onClick={() => {
                  setEditingOrderId(null)
                  setEditableRow(null)
                }}
              >
                <i className="ti ti-x text-danger fs-4"></i>
              </button>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <ActionButtons
                onView={() => {
                  setSelectedOrder(row)
                  setShowOrderInfo(true)
                }}
                onEdit={() => {
                  if (
                    [
                      'Completed',
                      'Cancelled',
                      'Out For Delivery',
                      'Preparing',
                    ].includes(row.status)
                  )
                    return
                  setEditingOrderId(row.orderNo)
                  setEditableRow(row)
                }}
                onDelete={() => dispatch(deleteLabOrder(row.orderNo))}
                disableEdit={[
                  'Completed',
                  'Cancelled',
                  'Out For Delivery',
                  'Preparing',
                ].includes(row.status)}
                disabledEditTooltip="This status cannot be edited"
              />
            </div>
          )}
        </td>
      ),
    },
  ]

  return (
    <>
      <div className="lab-tracker-section">
        <LabSharedTable
          columns={columns}
          data={filteredLabOrders}
          pageSize={10}
          onRowClick={(row, e) => {
            if ((e.target as HTMLElement).closest('.action-button')) {
              return
            }
            toggleRowExpansion(row.orderNo)
          }}
          rowClassName={(row) =>
            expandedRows.has(row.orderNo) ? 'expanded-row-open' : ''
          }
          additionalRows={(row) =>
            expandedRows.has(row.orderNo) ? (
              <tr className="expanded-row">
                <td colSpan={columns.length}>
                  <LabExpandedContent lab={row} />
                </td>
              </tr>
            ) : null
          }
        />
      </div>

      <LabOrderInfo
        show={showOrderInfo}
        onClose={() => {
          setSelectedOrder(null)
          setShowOrderInfo(false)
        }}
        labOrder={selectedOrder}
      />
    </>
  )
}

export default LabTracker
